import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { DeviceStatus } from './DeviceStatus'
import { Badge } from '@/components/ui/badge'
import { Star, Battery, Signal } from 'lucide-react'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import type { Device } from '@/types/device.types'
import { useToggleFavorite } from '@/hooks/useDevices'

interface DeviceCardProps {
  device: Device
}

export const DeviceCard = ({ device }: DeviceCardProps) => {
  const toggleFavorite = useToggleFavorite()

  const handleToggleFavorite = (e: React.MouseEvent) => {
    e.preventDefault()
    toggleFavorite.mutate(device.id)
  }

  return (
    <Link to={`/devices/${device.id}`}>
      <Card className="hover:shadow-lg transition-shadow cursor-pointer">
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <div className="space-y-1">
            <CardTitle className="text-lg">{device.name}</CardTitle>
            <p className="text-sm text-muted-foreground">{device.serialNumber}</p>
          </div>
          <div className="flex gap-2">
            <DeviceStatus status={device.status} />
            <Button
              variant="ghost"
              size="icon"
              onClick={handleToggleFavorite}
              className="h-8 w-8"
            >
              <Star
                className={`h-4 w-4 ${device.isFavorite ? 'fill-yellow-400 text-yellow-400' : ''}`}
              />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Type:</span>
            <Badge variant="outline">{device.deviceType}</Badge>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Account:</span>
            <span className="font-medium">{device.accountName}</span>
          </div>
          {device.batteryLevel && (
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-1 text-muted-foreground">
                <Battery className="h-4 w-4" />
                Battery:
              </span>
              <span className="font-medium">{device.batteryLevel}%</span>
            </div>
          )}
          {device.lastSeen && (
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-1 text-muted-foreground">
                <Signal className="h-4 w-4" />
                Last Seen:
              </span>
              <span className="font-medium">
                {format(new Date(device.lastSeen), 'MMM d, yyyy')}
              </span>
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  )
}
